import React, { useState, useEffect } from 'react';
import { Button, Space, message, Input, Upload } from 'antd';
import { PlusOutlined, SearchOutlined, FileUnknownOutlined, ReloadOutlined, InboxOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ModalForm, ProFormText, ProFormTextArea, ProFormDatePicker, ProFormDateTimePicker } from '@ant-design/pro-components';
import { Form } from 'antd';
import { renderDateTime, renderDate, ActionButtons, ResizableTable } from './utils';
import dayjs from 'dayjs';

interface Announcement {
  id: number;
  project_no: string;
  project_name: string;
  announcement_url?: string;
  announcement_text: string;
  content_pdf?: string;
  publish_time: string;
  file_get_start_time?: string;
  file_get_end_time?: string;
  create_time: string;
}

export default function BidOther() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<Announcement | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [form] = Form.useForm();
  const [pdfUrl, setPdfUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // 检查是否是项目经理
  const adminUser = localStorage.getItem('admin_user');
  const userInfo = adminUser ? JSON.parse(adminUser) : null;
  const isProjectManager = userInfo?.role_id === 4;

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/trade-announcements?type=4', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setAnnouncements(resp.data || []);
      } else {
        message.error(resp?.error || '获取其他公告列表失败');
      }
    } catch (err) {
      message.error('获取其他公告列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    setPdfUrl('');
    setModalVisible(true);
  };

  const handleEdit = (record: Announcement) => {
    setEditingItem(record);
    form.setFieldsValue({
      project_no: record.project_no,
      project_name: record.project_name,
      announcement_url: record.announcement_url || '',
      announcement_text: record.announcement_text,
      publish_time: record.publish_time ? dayjs(record.publish_time) : null,
      file_get_start_time: record.file_get_start_time ? dayjs(record.file_get_start_time) : undefined,
      file_get_end_time: record.file_get_end_time ? dayjs(record.file_get_end_time) : undefined,
    });
    setPdfUrl(record.content_pdf || '');
    setModalVisible(true);
  };

  const handleView = (record: Announcement) => {
    setEditingItem(record);
    form.setFieldsValue({
      project_no: record.project_no,
      project_name: record.project_name,
      announcement_url: record.announcement_url || '',
      announcement_text: record.announcement_text,
      publish_time: record.publish_time ? dayjs(record.publish_time) : null,
      file_get_start_time: record.file_get_start_time ? dayjs(record.file_get_start_time) : undefined,
      file_get_end_time: record.file_get_end_time ? dayjs(record.file_get_end_time) : undefined,
    });
    setPdfUrl(record.content_pdf || '');
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/trade-announcements/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('删除成功');
        fetchAnnouncements();
      } else {
        message.error(resp?.error || '删除失败');
      }
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (!pdfUrl || !pdfUrl.trim()) {
        message.error('请上传公告附件');
        return;
      }
      
      const token = localStorage.getItem('admin_token');
      const url = editingItem 
        ? `/api/admin/trade-announcements/${editingItem.id}`
        : '/api/admin/trade-announcements';
      const method = editingItem ? 'PUT' : 'POST';

      const submitData = {
        ...values,
        announcement_type: 4, // 其他公告
        content_pdf: pdfUrl,
        publish_time: values.publish_time ? values.publish_time.format('YYYY-MM-DD') : null,
        file_get_start_time: values.file_get_start_time?.format?.('YYYY-MM-DD HH:mm:ss'),
        file_get_end_time: values.file_get_end_time?.format?.('YYYY-MM-DD HH:mm:ss'),
      };

      console.log('提交数据:', submitData);

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(submitData),
      });
      
      console.log('响应状态:', response.status);
      const resp = await response.json();
      console.log('响应数据:', resp);

      if (resp && resp.ok) {
        message.success(editingItem ? '更新成功' : '创建成功');
        setModalVisible(false);
        setPdfUrl('');
        fetchAnnouncements();
      } else {
        message.error(resp?.error || '操作失败');
      }
    } catch (err: any) {
      console.error('提交错误:', err);
      if (err.errorFields) {
        message.error('请填写所有必填项');
      } else {
        message.error('操作失败: ' + (err?.message || '未知错误'));
      }
    }
  };

  const handleFileUpload = async (file: File) => {
    setUploading(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/upload', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      
      const resp = await response.json();

      if (resp && resp.ok) {
        setPdfUrl(resp.url);
        message.success('上传成功');
      } else {
        message.error(resp?.error || '上传失败');
      }
    } catch (err: any) {
      console.error('上传错误:', err);
      message.error('上传失败: ' + (err?.message || '网络错误'));
    } finally {
      setUploading(false);
    }
    
    return false;
  };

  // 过滤数据
  const filteredData = announcements.filter(a => 
    a.project_no?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    a.project_name?.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  const columns: ColumnsType<Announcement> = [
    {
      title: '公告编号',
      dataIndex: 'project_no',
      key: 'project_no',
      width: 150,
      render: (text) => <span style={{ fontFamily: 'monospace', color: '#1f2937' }}>{text}</span>
    },
    {
      title: '公告名称',
      dataIndex: 'project_name',
      key: 'project_name',
      ellipsis: true,
      render: (text) => <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
    },
    {
      title: '发布时间',
      dataIndex: 'publish_time',
      key: 'publish_time',
      width: 170,
      render: renderDate,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_, record) => (
        <ActionButtons
          onEdit={() => handleEdit(record)}
          onDelete={() => handleDelete(record.id)}
          onView={() => handleView(record)}
          deleteTitle="确定要删除这条其他公告吗？"
          showEdit={!isProjectManager}
          showDelete={!isProjectManager}
          showView={isProjectManager}
        />
      ),
    },
  ];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <FileUnknownOutlined />
          </div>
          <div>
            <h2>其他公告</h2>
            <p>管理所有其他公告信息</p>
          </div>
        </div>
        
        <Space size="middle">
          <Input
            placeholder="搜索公告编号/名称..."
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={{ width: 220, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchAnnouncements}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
          {!isProjectManager && (
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={handleAdd}
              style={{ 
                borderRadius: 10,
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                border: 'none',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
              }}
            >
              新增其他公告
            </Button>
          )}
        </Space>
      </div>
      
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{filteredData.length}</span> 条记录
        </div>
        
        <ResizableTable
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          className="admin-table"
          scroll={{ x: 'max-content' }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>
      
      <ModalForm
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FileUnknownOutlined style={{ color: 'white', fontSize: 16 }} />
            </div>
            <span>{editingItem ? '修改其他公告' : '新增其他公告'}</span>
          </div>
        }
        open={modalVisible}
        onOpenChange={setModalVisible}
        form={form}
        onFinish={isProjectManager ? undefined : handleSubmit}
        width={600}
        modalProps={{
          destroyOnHidden: true,
          centered: true,
        }}
        submitter={isProjectManager ? false : {
          searchConfig: { submitText: '确定', resetText: '取消' },
        }}
      >
        <div style={{ marginTop: 24 }}>
          <ProFormText
            name="project_no"
            label="公告编号"
            placeholder="请输入公告编号"
            rules={[{ required: true, message: '请输入公告编号' }]}
            fieldProps={{ disabled: isProjectManager }}
          />
          <ProFormText
            name="project_name"
            label="公告名称"
            placeholder="请输入公告名称"
            rules={[{ required: true, message: '请输入公告名称' }]}
            fieldProps={{ disabled: isProjectManager }}
          />
          <ProFormText
            name="announcement_url"
            label="公告链接"
            placeholder="请输入公告链接"
            fieldProps={{ disabled: isProjectManager }}
          />
          <Form.Item
            name="publish_time"
            label="发布时间"
            rules={[{ required: true, message: '请选择发布时间' }]}
          >
            <ProFormDatePicker
              placeholder="请选择发布时间"
              format="YYYY-MM-DD"
              fieldProps={{
                disabled: isProjectManager,
                style: { width: '100%' }
              }}
            />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ProFormDateTimePicker
              name="file_get_start_time"
              label="报名开始时间"
              placeholder="请选择时间"
              rules={[{ required: true, message: '请选择报名开始时间' }]}
              fieldProps={{ disabled: isProjectManager, style: { width: '100%' } }}
            />
            <ProFormDateTimePicker
              name="file_get_end_time"
              label="报名截止时间"
              placeholder="请选择时间"
              rules={[{ required: true, message: '请选择报名截止时间' }]}
              fieldProps={{ disabled: isProjectManager, style: { width: '100%' } }}
            />
          </div>
          <ProFormTextArea
            name="announcement_text"
            label="公告内容"
            placeholder="请输入公告内容"
            rules={[{ required: true, message: '请输入公告内容' }]}
            fieldProps={{
              rows: 6,
              showCount: true,
              maxLength: 5000,
              disabled: isProjectManager,
            }}
          />
          <Form.Item 
            label={
              <span>
                公告附件
                <span style={{ color: '#ff4d4f', marginLeft: 8, fontSize: 12 }}>上传PDF文件尽量在50M内</span>
              </span>
            } 
            required
          >
            <Upload.Dragger
              name="file"
              accept=".pdf"
              showUploadList={false}
              beforeUpload={handleFileUpload}
              disabled={uploading || isProjectManager}
            >
              {pdfUrl ? (
                <div style={{ padding: '20px' }}>
                  <p style={{ color: '#52c41a', marginBottom: 8 }}>✓ 已上传附件</p>
                  <p style={{ color: '#3b82f6', marginTop: 8 }}><a href={pdfUrl} target="_blank">点击预览</a></p>
                  <p style={{ color: '#999', fontSize: 12 }}>{pdfUrl}</p>
                  <p style={{ color: '#3b82f6', marginTop: 8 }}>点击重新上传</p>
                  
                </div>
              ) : (
                <div style={{ padding: '20px' }}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined style={{ color: '#3b82f6', fontSize: 48 }} />
                  </p>
                  <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
                  <p className="ant-upload-hint">仅支持 PDF 格式文件</p>
                </div>
              )}
            </Upload.Dragger>
          </Form.Item>
        </div>
      </ModalForm>
    </div>
  );
}
